import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import useSWR from 'swr';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import is from 'date-fns/locale/is';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import { AddAlert } from '@mui/icons-material';

registerLocale('is', is);

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SingleImageUpload from 'components/SingleImageUpload/SingleImageUpload';
import SnackbarCustom from 'components/Snackbar/Snackbar.js';
import SparaLoader from 'components/SparaLoader/SparaLoader.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaSwitch from 'components/SparaSwitch/SparaSwitch';
import { FlexContainer } from 'components/FlexContainer/FlexContainer.jsx';

import api, { companyUserApi, authApi, regionApi, userApi, fetcher } from 'lib/api';
import { formatPhoneNumber } from 'utils/phoneFormatter';
import { formatKennitala } from 'utils/kennitalaFormatter';
import Passkeys from 'components/Passkeys/Passkeys';

const style = {
    pageContainer: {
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '0 20px',
    },
    sectionHeading: {
        marginLeft: '10px',
        marginBottom: '5px',
        fontWeight: 'bold',
        fontStyle: 'italic',
        color: '#de2c49',
    },
    inputContainer: {
        marginBottom: '24px',
        '& .MuiInputBase-root': {
            padding: '12px 0',
        },
    },
    sparaField: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            fontSize: '14px',
            height: '40px',
            '& fieldset': { borderColor: '#d2d2d2' },
            '&:hover fieldset': { borderColor: '#aaa' },
            '&.Mui-focused fieldset': { borderColor: '#de2c49', borderWidth: '1px' },
        },
        '& .MuiInputLabel-root': {
            fontSize: '14px',
            color: '#AAAAAA',
            '&.Mui-focused': { color: '#de2c49' },
        },
        '& .MuiSelect-select': { fontSize: '14px' },
        '& .react-datepicker-wrapper': { width: '100%' },
        '& .react-datepicker__input-container': { width: '100%' },
        '& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected': {
            backgroundColor: '#de2c49',
            borderRadius: '50%',
        },
        '& .react-datepicker__day--selected:hover, & .react-datepicker__day--keyboard-selected:hover': {
            backgroundColor: '#b52038',
        },
        '& .react-datepicker__header': {
            backgroundColor: '#fff',
            borderBottom: '1px solid #f0f0f0',
        },
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    infoBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '20px',
    },
    infoLabel: {
        fontSize: '11px',
        color: '#AAAAAA',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontWeight: '400',
        margin: 0,
    },
    infoValue: {
        fontSize: '14px',
        color: '#555',
        margin: 0,
    },
    infoName: {
        fontSize: '22px',
        fontWeight: '600',
        color: '#333',
        margin: 0,
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
    },
    actionBtn: {
        flexShrink: 0,
        fontSize: '12px',
        color: '#de2c49',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        fontWeight: '600',
        textDecoration: 'underline',
        '&:hover': {
            color: '#b52038',
        },
    },
    unverifiedWarning: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        color: '#e67e22',
        margin: '3px 0 0 0',
        fontWeight: '500',
    },
    verifyLink: {
        flexShrink: 0,
        fontSize: '12px',
        color: '#e67e22',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        fontWeight: '600',
        textDecoration: 'underline',
        '&:hover': {
            color: '#c0640e',
        },
    },
    dialogInfo: {
        fontSize: '13px',
        color: '#888',
        marginTop: '8px',
    },
    dialogError: {
        fontSize: '13px',
        color: '#de2c49',
        marginTop: '4px',
    },
};

const useStyles = createUseStyles(style);

const Profile = ({ t }) => {
    const classes = useStyles();
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', type: '' });
    const [emailDialog, setEmailDialog] = useState({
        open: false,
        newEmail: '',
        error: '',
        isSubmitting: false,
    });
    const [phoneDialog, setPhoneDialog] = useState({
        open: false,
        step: 'change',
        newPhone: '',
        code: '',
        error: '',
        isSubmitting: false,
        verifyOnly: false,
    });
    const [isDirty, setIsDirty] = useState(false);
    const isDirtyRef = useRef(false);
    const originalPictureRef = useRef('');
    const [blockedUrl, setBlockedUrl] = useState(null);
    const [showNavBlocker, setShowNavBlocker] = useState(false);
    const navigate = useNavigate();

    const markDirty = (val) => {
        isDirtyRef.current = val;
        setIsDirty(val);
    };

    const userStore = JSON.parse(localStorage.getItem('user'));
    const isSuperAdmin = userStore?.roles?.includes('super_admin');
    const companyUserId = userStore?.user?.id;

    const { data: meData, isLoading: isLoadingUser, mutate: mutateUser } = useSWR(
        authApi.me,
        fetcher,
        { revalidateOnFocus: false }
    );

    const { data: regionsData, isLoading: isLoadingRegions } = useSWR(regionApi.list, fetcher, {
        revalidateOnFocus: false,
    });

    const [formState, setFormState] = useState({
        profile_picture: '',
        name: '',
        email: '',
        email_verified: true,
        phone: '',
        phone_verified: true,
        kennitala: '',
        date_of_birth: null,
        torgid_region_id: '',
        postalcode_number: '',
        postalcode_city: '',
        language: 'IS',
        open_for_barter: false,
        hidden: false,
    });

    useEffect(() => {
        const user = meData?.results;
        if (!user) return;

        const cu = user.torgid_company_users?.[0] || {};
        const regions = regionsData?.results || [];
        const region = regions.find((r) => r.id === user.torgid_region_id);

        originalPictureRef.current = user.profile_picture || '';
        setFormState({
            profile_picture: user.profile_picture || '',
            name: user.name || '',
            email: user.email || '',
            email_verified: user.email_verified ?? true,
            phone: user.phone || '',
            phone_verified: user.phone_verified ?? true,
            kennitala: user.kennitala || '',
            date_of_birth: user.date_of_birth || null,
            torgid_region_id: user.torgid_region_id || '',
            postalcode_number: region?.postal_code || '',
            postalcode_city: region?.city || '',
            language: user.language?.toUpperCase() || 'IS',
            open_for_barter: cu.open_for_barter || false,
            hidden: cu.hidden || false,
        });
    }, [meData, regionsData]);

    const change = (field, value) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
        markDirty(true);
    };

    const handleChangeRegion = (region) => {
        if (!region) return;
        setFormState((prev) => ({
            ...prev,
            torgid_region_id: region.id,
            postalcode_number: region.postal_code,
            postalcode_city: region.city,
        }));
        markDirty(true);
    };

    const onSubmit = async () => {
        setIsSaving(true);
        try {
            const roleIds = [];
            if (userStore.roles?.length === 1 && userStore.roles[0] === 'group_admin') {
                roleIds.push(28);
            } else if (userStore.roles?.length === 1 && userStore.roles[0] === 'company_user') {
                roleIds.push(4);
            } else if (
                userStore.roles?.length > 1 &&
                userStore.roles.includes('company_user') &&
                userStore.roles.includes('group_admin')
            ) {
                roleIds.push(4, 28);
            }

            const userId = meData?.results?.id;
            const userUpdatePayload = {
                date_of_birth: formState.date_of_birth,
                torgid_region_id: formState.torgid_region_id || null,
                language: formState.language?.toLowerCase(),
                ...(formState.profile_picture?.startsWith('data:')
                    ? { profile_picture: formState.profile_picture }
                    : formState.profile_picture === '' && originalPictureRef.current !== ''
                    ? { profile_picture: null }
                    : {}),
            };

            if (isSuperAdmin) {
                await api.put(userApi.update(userId), userUpdatePayload);
            } else {
                await Promise.all([
                    api.put(companyUserApi.update(companyUserId), {
                        torgid_company_ids: userStore.companyIds,
                        group_ids: userStore.groupIds,
                        role_ids: roleIds,
                        torgid_user_id: userId,
                        open_for_barter: formState.open_for_barter,
                        hidden: formState.hidden,
                    }),
                    api.put(userApi.update(userId), userUpdatePayload),
                ]);
            }

            await mutateUser();
            markDirty(false);
            setNotification({
                open: true,
                message: t('profile.save_success'),
                type: 'success',
            });
        } catch (error) {
            console.error('Error saving profile:', error);
            setNotification({
                open: true,
                message: t('profile.save_error'),
                type: 'warning',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const openEmailDialog = () => {
        setEmailDialog({ open: true, newEmail: '', error: '', isSubmitting: false });
    };

    const closeEmailDialog = () => {
        setEmailDialog((prev) => ({ ...prev, open: false }));
    };

    const handleChangeEmail = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailDialog.newEmail)) {
            setEmailDialog((prev) => ({ ...prev, error: t('profile.email_invalid') }));
            return;
        }
        if (emailDialog.newEmail === formState.email && formState.email_verified) {
            setEmailDialog((prev) => ({ ...prev, error: t('profile.email_same') }));
            return;
        }
        setEmailDialog((prev) => ({ ...prev, isSubmitting: true, error: '' }));
        try {
            await api.post(userApi.changeEmail, { email: emailDialog.newEmail });
            closeEmailDialog();
            setNotification({
                open: true,
                message: t('profile.change_email_success'),
                type: 'success',
            });
        } catch (error) {
            const msg = error?.response?.data?.message || t('profile.change_email_error');
            setEmailDialog((prev) => ({ ...prev, isSubmitting: false, error: msg }));
        }
    };

    const handleResendEmailVerification = async () => {
        try {
            await api.post(userApi.changeEmail, { email: formState.email });
            setNotification({
                open: true,
                message: t('profile.change_email_success'),
                type: 'success',
            });
        } catch (error) {
            setNotification({
                open: true,
                message: t('profile.change_email_error'),
                type: 'warning',
            });
        }
    };

    const openPhoneDialog = () => {
        setPhoneDialog({
            open: true,
            step: 'change',
            newPhone: '',
            code: '',
            error: '',
            isSubmitting: false,
            verifyOnly: false,
        });
    };

    const closePhoneDialog = () => {
        setPhoneDialog((prev) => ({ ...prev, open: false }));
    };

    const handleSendPhoneCode = async () => {
        setPhoneDialog((prev) => ({ ...prev, isSubmitting: true, error: '' }));
        try {
            await api.post(userApi.changePhone, { phone: phoneDialog.newPhone });
            setPhoneDialog((prev) => ({ ...prev, isSubmitting: false, step: 'verify' }));
        } catch (error) {
            const msg = error?.response?.data?.message || t('profile.change_phone_error');
            setPhoneDialog((prev) => ({ ...prev, isSubmitting: false, error: msg }));
        }
    };

    const handleVerifyPhoneCode = async () => {
        setPhoneDialog((prev) => ({ ...prev, isSubmitting: true, error: '' }));
        try {
            await api.post(userApi.verifyByCode, {
                phone: phoneDialog.newPhone,
                code: phoneDialog.code,
            });
            closePhoneDialog();
            await mutateUser();
            setNotification({
                open: true,
                message: t('profile.change_phone_success'),
                type: 'success',
            });
        } catch (error) {
            const msg = error?.response?.data?.message || t('profile.change_phone_code_error');
            setPhoneDialog((prev) => ({ ...prev, isSubmitting: false, error: msg }));
        }
    };

    const handleResendPhoneVerification = async () => {
        setPhoneDialog({
            open: true,
            step: 'verify',
            newPhone: formState.phone,
            code: '',
            error: '',
            isSubmitting: true,
            verifyOnly: true,
        });
        try {
            await api.post(userApi.changePhone, { phone: formState.phone });
            setPhoneDialog((prev) => ({ ...prev, isSubmitting: false }));
        } catch (error) {
            const msg = error?.response?.data?.message || t('profile.change_phone_error');
            setPhoneDialog((prev) => ({ ...prev, isSubmitting: false, error: msg }));
        }
    };

    useEffect(() => {
        const originalPushState = window.history.pushState;
        let isInternal = false;

        window.history.pushState = function (state, title, url) {
            if (isInternal || !isDirtyRef.current) {
                originalPushState.call(this, state, title, url);
                return;
            }
            setBlockedUrl(url?.toString() ?? null);
            setShowNavBlocker(true);
        };

        const handlePopState = () => {
            if (!isDirtyRef.current) return;
            isInternal = true;
            originalPushState.call(window.history, null, '', window.location.href);
            isInternal = false;
            setBlockedUrl(null);
            setShowNavBlocker(true);
        };

        const handleBeforeUnload = (e) => {
            if (!isDirtyRef.current) return;
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.history.pushState = originalPushState;
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const confirmLeave = useCallback(() => {
        markDirty(false);
        setShowNavBlocker(false);
        if (blockedUrl) navigate(blockedUrl);
    }, [blockedUrl, navigate]);

    const stayOnPage = useCallback(() => {
        setShowNavBlocker(false);
        setBlockedUrl(null);
    }, []);

    const regions = regionsData?.results || [];
    const regionOptions = regions.map((option) => {
        const firstLetter = option.torgid_region_name?.name?.toUpperCase() || '';
        return {
            firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
            ...option,
        };
    });

    if (isLoadingUser || isLoadingRegions) return <SparaLoader text={t('common.loading')} />;

    return (
        <div className={classes.pageContainer}>
            <GridContainer>
                <GridItem xs={12}>
                    <SparaTitle
                        title={t('menu.my_profile')}
                        rightComponent={
                            <SparaButton color="primary" onClick={onSubmit} disabled={isSaving}>
                                {t('save')}
                            </SparaButton>
                        }
                    />

                    {/* Basic Info */}
                    <h3 className={classes.sectionHeading}>{t('profile.basic_info')}</h3>
                    <Card style={{ margin: '0px' }}>
                        <CardBody>
                            <GridContainer justifyContent="space-between">
                                <GridItem xs={12} style={{ marginBottom: '20px' }}>
                                    <GridContainer>
                                        <GridItem xs={12} sm={4}>
                                            <SingleImageUpload
                                                image={formState.profile_picture}
                                                onUpload={(data) => change('profile_picture', data)}
                                                onDelete={() => change('profile_picture', '')}
                                                onError={() => change('profile_picture', '')}
                                                cropShape="round"
                                                aspectRatio={1}
                                            />
                                        </GridItem>
                                        <GridItem xs={12} sm={8}>
                                            <div className={classes.infoBlock}>
                                                <p className={classes.infoName}>{formState.name}</p>
                                            </div>
                                            <div className={classes.infoBlock}>
                                                <p className={classes.infoLabel}>
                                                    {t('kennitala')}
                                                </p>
                                                <p className={classes.infoValue}>
                                                    {formatKennitala(formState.kennitala)}
                                                </p>
                                            </div>
                                            <div className={classes.infoBlock}>
                                                <p className={classes.infoLabel}>
                                                    {t('phone_number')}
                                                </p>
                                                <div className={classes.infoRow}>
                                                    <p className={classes.infoValue}>
                                                        {formatPhoneNumber(formState.phone)}
                                                    </p>
                                                    <button
                                                        className={classes.actionBtn}
                                                        onClick={openPhoneDialog}
                                                        type="button"
                                                    >
                                                        {t('profile.change_phone')}
                                                    </button>
                                                </div>
                                                {!formState.phone_verified && (
                                                    <p className={classes.unverifiedWarning}>
                                                        ⚠ {t('profile.phone_not_verified')}
                                                        <button
                                                            className={classes.verifyLink}
                                                            onClick={handleResendPhoneVerification}
                                                            type="button"
                                                        >
                                                            {t('profile.verify')}
                                                        </button>
                                                    </p>
                                                )}
                                            </div>
                                            <div className={classes.infoBlock}>
                                                <p className={classes.infoLabel}>{t('email')}</p>
                                                <div className={classes.infoRow}>
                                                    <p className={classes.infoValue}>
                                                        {formState.email}
                                                    </p>
                                                    <button
                                                        className={classes.actionBtn}
                                                        onClick={openEmailDialog}
                                                        type="button"
                                                    >
                                                        {t('profile.change_email')}
                                                    </button>
                                                </div>
                                                {!formState.email_verified && (
                                                    <p className={classes.unverifiedWarning}>
                                                        ⚠ {t('profile.email_not_verified')}
                                                        <button
                                                            className={classes.verifyLink}
                                                            onClick={handleResendEmailVerification}
                                                            type="button"
                                                        >
                                                            {t('profile.verify')}
                                                        </button>
                                                    </p>
                                                )}
                                            </div>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>

                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <div className={classes.sparaField}>
                                        <DatePicker
                                            selected={
                                                formState.date_of_birth
                                                    ? new Date(formState.date_of_birth)
                                                    : null
                                            }
                                            onChange={(date) =>
                                                change(
                                                    'date_of_birth',
                                                    date ? date.toISOString().split('T')[0] : null
                                                )
                                            }
                                            dateFormat="dd.MM.yyyy"
                                            showYearDropdown
                                            showMonthDropdown
                                            dropdownMode="select"
                                            maxDate={new Date()}
                                            locale="is"
                                            yearDropdownItemNumber={80}
                                            scrollableYearDropdown
                                            customInput={
                                                <TextField
                                                    label={t('date_of_birth')}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    InputProps={{ readOnly: true }}
                                                />
                                            }
                                        />
                                    </div>
                                </GridItem>
                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <div className={classes.sparaField}>
                                        <Autocomplete
                                            id="region"
                                            onChange={(event, newValue) =>
                                                handleChangeRegion(newValue)
                                            }
                                            value={
                                                formState.torgid_region_id &&
                                                formState.postalcode_number
                                                    ? {
                                                          id: formState.torgid_region_id,
                                                          postal_code: formState.postalcode_number,
                                                          city: formState.postalcode_city,
                                                      }
                                                    : null
                                            }
                                            options={regionOptions.sort(
                                                (a, b) =>
                                                    -b.firstLetter.localeCompare(a.firstLetter)
                                            )}
                                            groupBy={(option) => option.firstLetter}
                                            getOptionLabel={(option) =>
                                                option.postal_code + ' ' + option.city
                                            }
                                            isOptionEqualToValue={(option, value) =>
                                                option.id === value.id
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('select_region')}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </div>
                                </GridItem>
                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <div className={classes.sparaField}>
                                        <FormControl fullWidth size="small" variant="outlined">
                                            <InputLabel>{t('choose_language')}</InputLabel>
                                            <Select
                                                value={formState.language}
                                                onChange={(e) => change('language', e.target.value)}
                                                label={t('choose_language')}
                                            >
                                                <MenuItem value="IS">{t('icelandic')}</MenuItem>
                                                <MenuItem value="EN">{t('english')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>
                                </GridItem>
                            </GridContainer>
                        </CardBody>
                    </Card>
                </GridItem>

                {/* Options - hidden for now
                <GridItem xs={12}>
                    <h3 className={classes.sectionHeading}>{t('profile.options')}</h3>
                    <Card style={{ margin: '0px' }}>
                        <CardBody
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '20px',
                                gap: 10,
                            }}
                        >
                            <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
                                <SparaSwitch
                                    label={t('company_user.open_to_exchange')}
                                    subtitle={
                                        formState.open_for_barter
                                            ? t('company_user.open_to_exchange_info')
                                            : t('company_user.not_open_to_exchange_info')
                                    }
                                    checked={formState.open_for_barter}
                                    onChange={(e) =>
                                        change('open_for_barter', e.target.checked)
                                    }
                                    labelProps={{
                                        onClick: () =>
                                            change(
                                                'open_for_barter',
                                                !formState.open_for_barter
                                            ),
                                    }}
                                />
                            </FlexContainer>

                            <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
                                <SparaSwitch
                                    label={t('company_user.hidden')}
                                    subtitle={
                                        formState.hidden
                                            ? t('company_user.hidden_info')
                                            : t('company_user.visible_info')
                                    }
                                    checked={formState.hidden}
                                    onChange={(e) => change('hidden', e.target.checked)}
                                    labelProps={{
                                        onClick: () =>
                                            change('hidden', !formState.hidden),
                                    }}
                                />
                            </FlexContainer>
                        </CardBody>
                    </Card>
                </GridItem>
                */}

                <GridItem xs={12}>
                    <h3 className={classes.sectionHeading}>{t('profile.passkeys')}</h3>
                    <Card style={{ margin: '0px' }}>
                        <CardBody style={{ padding: '20px' }}>
                            <Passkeys
                                userPhone={formState.phone}
                                userKennitala={formState.kennitala}
                                hasPassword={!!meData?.results?.has_password}
                                onNotify={(message, type = 'success') =>
                                    setNotification({ open: true, message, type })
                                }
                            />
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>

            {isSaving && (
                <div className={classes.overlay}>
                    <SparaLoader text={t('common.saving')} />
                </div>
            )}

            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={notification.type === 'success' ? 'success' : 'warning'}
                message={notification.message}
                open={notification.open}
                closeNotification={() => setNotification((prev) => ({ ...prev, open: false }))}
            />

            <Dialog open={emailDialog.open} onClose={closeEmailDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{t('profile.change_email')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('profile.new_email')}
                        type="email"
                        fullWidth
                        variant="standard"
                        value={emailDialog.newEmail}
                        onChange={(e) =>
                            setEmailDialog((prev) => ({
                                ...prev,
                                newEmail: e.target.value,
                                error: '',
                            }))
                        }
                        onKeyDown={(e) => e.key === 'Enter' && handleChangeEmail()}
                        error={!!emailDialog.error}
                    />
                    {emailDialog.error && (
                        <p className={classes.dialogError}>{emailDialog.error}</p>
                    )}
                    <p className={classes.dialogInfo}>{t('profile.change_email_info')}</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEmailDialog} disabled={emailDialog.isSubmitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleChangeEmail}
                        disabled={emailDialog.isSubmitting}
                        variant="contained"
                        color="error"
                    >
                        {emailDialog.isSubmitting ? t('common.saving') : t('profile.change_email')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={phoneDialog.open} onClose={closePhoneDialog} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {phoneDialog.verifyOnly
                        ? t('profile.verify_phone')
                        : phoneDialog.step === 'change'
                        ? t('profile.change_phone')
                        : t('profile.verify_phone_code')}
                </DialogTitle>
                <DialogContent>
                    {phoneDialog.step === 'change' ? (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label={t('profile.new_phone')}
                                type="tel"
                                fullWidth
                                variant="standard"
                                value={phoneDialog.newPhone}
                                onChange={(e) =>
                                    setPhoneDialog((prev) => ({
                                        ...prev,
                                        newPhone: e.target.value,
                                        error: '',
                                    }))
                                }
                                onKeyDown={(e) => e.key === 'Enter' && handleSendPhoneCode()}
                                error={!!phoneDialog.error}
                            />
                            {phoneDialog.error && (
                                <p className={classes.dialogError}>{phoneDialog.error}</p>
                            )}
                            <p className={classes.dialogInfo}>{t('profile.change_phone_info')}</p>
                        </>
                    ) : (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label={t('profile.sms_code')}
                                type="text"
                                fullWidth
                                variant="standard"
                                value={phoneDialog.code}
                                onChange={(e) =>
                                    setPhoneDialog((prev) => ({
                                        ...prev,
                                        code: e.target.value,
                                        error: '',
                                    }))
                                }
                                onKeyDown={(e) => e.key === 'Enter' && handleVerifyPhoneCode()}
                                error={!!phoneDialog.error}
                            />
                            {phoneDialog.error && (
                                <p className={classes.dialogError}>{phoneDialog.error}</p>
                            )}
                            <p className={classes.dialogInfo}>{t('profile.sms_code_info')}</p>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePhoneDialog} disabled={phoneDialog.isSubmitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={
                            phoneDialog.step === 'change'
                                ? handleSendPhoneCode
                                : handleVerifyPhoneCode
                        }
                        disabled={phoneDialog.isSubmitting}
                        variant="contained"
                        color="error"
                    >
                        {phoneDialog.isSubmitting
                            ? t('common.saving')
                            : phoneDialog.step === 'change'
                            ? t('profile.send_code')
                            : t('profile.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showNavBlocker} maxWidth="xs" fullWidth>
                <DialogTitle>{t('profile.unsaved_changes_title')}</DialogTitle>
                <DialogContent>
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--spara-text, #333)' }}>
                        {t('profile.unsaved_changes_message')}
                    </p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={stayOnPage}>{t('profile.unsaved_stay')}</Button>
                    <Button onClick={confirmLeave} color="error">
                        {t('profile.unsaved_leave')}
                    </Button>
                    <Button
                        onClick={async () => {
                            await onSubmit();
                            confirmLeave();
                        }}
                        variant="contained"
                        color="success"
                    >
                        {t('save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default withTranslation('', { withRef: true })(Profile);
